import React, { useEffect, useState } from "react";
import {
  AddDocument,
  fetchDocument,
  ViewDocument,
  updateDocument
} from "../../services/DocumentService/DocumentService.js";
import moment from "moment/moment.js";
import { motion } from "framer-motion";
import Loader from "../../components/Loader/Loader.jsx";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";
import { ToastContainer } from "react-toastify";

export default function DocumentMaster() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [groupOtpions, setGroupOptions] = useState([]);

  const [formData, setFormData] = useState({
    name: "",
    kyc_applicable: "INDIVIDUAL",
    status: true
  });

  // ---------------- LOAD LIST ----------------
  const loadDocuments = async () => {
    setLoading(true);
    try {
      const res = await fetchDocument();
      setDocuments(res?.data || []);
      setGroupOptions(
        Array.from(
          new Set(
            (res?.data || []).map((doc) => doc.group).filter((g) => g)
          )
        )
      );
     // console.log("Group Options:", groupOtpions);
    } catch (err) {
      console.error("Failed to fetch documents", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDocuments();
  }, []);

  // ---------------- ADD ----------------
  const openAddModal = () => {
    setEditId(null);
    setFormData({
      name: "",
      kyc_applicable: "INDIVIDUAL",
      status: true
    });
    setModalOpen(true);
  };

  // ---------------- EDIT ----------------
  const openEditModal = async (id) => {
    setLoading(true);
    try {
      const res = await ViewDocument({ id });

      const d = res?.data;
      if (!d) return;

      setEditId(id);

      setFormData({
        name: d.name,
        kyc_applicable: d.kyc_applicable,
        status: d.status?.value === "active",
        is_required: d.is_required,
        group: d.group
      });

      setModalOpen(true);
    } catch (err) {
      console.error("Failed to fetch view data", err);
    } finally {
      setLoading(false);
    }
  };

  // ---------------- SUBMIT ----------------

  const handleSubmit = async () => {
  setLoading(true);

  try {
    const payload = {
      name: formData.name,
      kyc_applicable: formData.kyc_applicable,
      status: formData.status,
      is_required: formData.is_required ?? 0,
      group: formData.group,
    };

    if (editId) {
      await updateDocument({
        id: editId,
        payload,
      });
      showSuccessToast("Document updated successfully");
    } else {
      await AddDocument(payload);
      showSuccessToast("Document added successfully");
    }

    setModalOpen(false);
    loadDocuments();

  } catch (err) {
    console.error("Submit failed", err);

    // ✅ Handle validation error (422)
    if (err?.response?.status === 422) {
      const errors = err.response.data.errors;

      // Show first validation error
      const firstError =
        errors && Object.values(errors)[0] && Object.values(errors)[0][0];

      showErrorToast(firstError || "Validation failed");
    } else {
      showErrorToast("Something went wrong. Please try again.");
    }

  } finally {
    setLoading(false);
  }
};


  return (
    <div className="p-4 md:p-4 max-w-5xl mx-auto mt-16 bg-white rounded-md">
       {loading && <Loader />}
      {/* HEADER */}
      <div className="flex justify-between items-center mb-2">
        <h1 className="text-xl md:text-2xl font-semibold">KYC Document Master</h1>

        <button
          onClick={openAddModal}
          className="bg-blue-600 text-white rounded-lg px-4 py-2"
        >
          + Add Document
        </button>
      </div>

      {/* TABLE */}
      {loading ? (
        <p className="text-center">Loading...</p>
      ) : (
        <div className="overflow-auto rounded-lg border shadow-sm bg-white">
          <table className="w-full text-sm">
            <thead className="bg-gray-100 text-gray-700">
              <tr>
                <th className="p-2 text-left">ID</th>
                <th className="p-2 text-left">Document Name</th>
                <th className="p-2 text-left">KYC Applicable</th>
                <th className="p-2 text-left">Required</th>
                <th className="p-2 text-left">Group</th>
                <th className="p-2 text-left">Status</th>
                <th className="p-2 text-left">Updated At</th>
                <th className="p-2 text-center">Action</th>
              </tr>
            </thead>

            <tbody>
              {documents.map((doc) => (
                <tr
                  key={doc.id}
                  className="border-t hover:bg-gray-50 transition-all"
                >
                  <td className="p-2">{doc.id}</td>
                  <td className="p-2">{doc.name}</td>
                  <td className="p-2">{doc.kyc_applicable}</td>
                  <td className="p-2">
                      {doc.is_required == 1 ? "Required" : "Optional"}
                  </td>
                  <td className="p-2">{doc.group}</td>
                  <td className="p-2">
                    <span
                      className={`px-2 py-1 rounded text-white text-xs ${
                        doc.status?.value === "active"
                          ? "bg-green-600"
                          : "bg-red-600"
                      }`}
                    >
                      {doc.status?.value}
                    </span>
                  </td>
                  <td className="p-2">
                   {moment(doc.updated_at).format("DD-MM-YYYY hh:mm A")}

                  </td>

                  <td className="p-3 text-center">
                    <button
                      onClick={() => openEditModal(doc.id)}
                      className="text-white bg-yellow-600 rounded-md px-1 py-0 hover:underline"
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* MODAL */}
      {modalOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/40 flex justify-center items-center p-4"
        >
          <motion.div
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            className="bg-white rounded-xl p-6 w-full max-w-md shadow-lg"
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">
                {editId ? "Edit Document" : "Add Document"}
              </h2>
              <button onClick={() => setModalOpen(false)}>✕</button>
            </div>

      {/* FORM */}
<div className="space-y-4">

  {/* Document Name */}
  <input
    type="text"
    placeholder="Document Name"
    className="w-full border rounded-lg px-3 py-2"
    value={formData.name}
    onChange={(e) =>
      setFormData({ ...formData, name: e.target.value })
    }
  />

  {/* KYC Applicable */}
  <select
    className="w-full border rounded-lg px-3 py-2"
    value={formData.kyc_applicable}
    onChange={(e) =>
      setFormData({
        ...formData,
        kyc_applicable: e.target.value
      })
    }
  >
    <option value="INDIVIDUAL">INDIVIDUAL</option>
    <option value="BUSINESS">BUSINESS</option>
  </select>

  {/* Status */}
  <select
    className="w-full border rounded-lg px-3 py-2"
    value={formData.status}
    onChange={(e) =>
      setFormData({
        ...formData,
        status: e.target.value === "true"
      })
    }
  >
    <option value="true">Active</option>
    <option value="false">Inactive</option>
  </select>

  {/* Is Required */}
  <div className="flex items-center gap-2">
    <input
      type="checkbox"
      checked={formData.is_required}
      onChange={(e) =>
        setFormData({
          ...formData,
          is_required: e.target.checked
        })
      }
    />
    <label className="text-sm font-medium">Is Required</label>
  </div>

  {/* Group */}
<select
  className="w-full border rounded-lg px-3 py-2 mb-2"
  value={formData.group || ""}
  onChange={(e) =>
    setFormData({ ...formData, group: e.target.value })
  }
>
  <option value="">Select Existing Group</option>

  {groupOtpions.map((group, index) => (
    <option key={index} value={group}>
      {group}
    </option>
  ))}
</select>


  {/* Submit Button */}
  <button
    className="w-full bg-blue-600 text-white rounded-lg py-2 mt-2"
    onClick={handleSubmit}
  >
    {editId ? "Update Document" : "Add Document"}
  </button>

</div>
   <ToastContainer />
          </motion.div>
        </motion.div>
      )}
    </div>
  );
}
